import { useQuery } from '@tanstack/react-query'
import type { NotificationTemplateDraft } from '@today/ui-domain'
import { useState } from 'react'
import { useNotificationTemplates } from '@/hooks/notification-templates/useNotificationTemplates'
import { useTemplatePreview } from '@/hooks/notification-templates/useTemplatePreview'
import { offerService } from '@/services/offer.service'
import { draftOf, EMPTY_DRAFT, inputOf, subjectOf, templateSaveErrorOf } from './template-draft'

/** How many of the tenant's offers the preview picker offers (most recent page). */
const PREVIEW_OFFER_LIMIT = 50

interface Editing {
	id: string | null
	draft: NotificationTemplateDraft
}

/** The tenant's offers a preview can render against, read once the form opens. */
function usePreviewOffers(enabled: boolean) {
	const offers = useQuery({
		queryKey: ['notification-templates', 'preview-offers'],
		queryFn: () => offerService.list({ page: 1, limit: PREVIEW_OFFER_LIMIT }),
		enabled,
	})
	return (offers.data?.data ?? []).map((offer) => ({
		value: offer.offerId,
		label: `${offer.status} · ${offer.locale} · ${offer.offerId.slice(0, 8)}`,
	}))
}

/** The open form: its values, the preview offer, the preview and the refused-save message. */
function useEditor() {
	const [editing, setEditing] = useState<Editing | null>(null)
	const [previewOfferId, setPreviewOfferId] = useState('')
	const [saveError, setSaveError] = useState<string | null>(null)
	const preview = useTemplatePreview()
	const open = (next: Editing | null) => {
		setEditing(next)
		setSaveError(null)
		preview.reset()
	}
	return { editing, setEditing, previewOfferId, setPreviewOfferId, saveError, setSaveError, preview, open }
}

/**
 * The template library page (TO-140 offer-notification-templates REQ-011): the
 * list, a create/edit form whose unsaved text previews against a picked offer,
 * saves that keep the input and show the refusal, and confirmed deletes.
 */
export function useNotificationTemplatesPage() {
	const library = useNotificationTemplates()
	const editor = useEditor()
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
	const offerOptions = usePreviewOffers(editor.editing !== null)
	const { editing } = editor

	const save = () => {
		if (!editing) return
		editor.setSaveError(null)
		library.save.mutate(
			{ id: editing.id, input: inputOf(editing.draft) },
			{ onSuccess: () => editor.open(null), onError: (error) => editor.setSaveError(templateSaveErrorOf(error)) },
		)
	}
	const runPreview = () => {
		if (!editing || editor.previewOfferId === '') return
		const { draft } = editing
		editor.preview.run({ offerId: editor.previewOfferId, subject: subjectOf(draft), body: draft.body })
	}
	const startEdit = (id: string) => {
		const template = library.templates.find((t) => t.id === id)
		if (template) editor.open({ id, draft: draftOf(template) })
	}
	const confirmDelete = () => {
		if (pendingDeleteId) library.remove.mutate(pendingDeleteId, { onSettled: () => setPendingDeleteId(null) })
	}

	return {
		library,
		editing,
		offerOptions,
		previewOfferId: editor.previewOfferId,
		preview: editor.preview,
		saveError: editor.saveError,
		isSaving: library.save.isPending,
		pendingDeleteId,
		startCreate: () => editor.open({ id: null, draft: EMPTY_DRAFT }),
		startEdit,
		change: (draft: NotificationTemplateDraft) => editor.setEditing(editing ? { ...editing, draft } : null),
		cancel: () => editor.open(null),
		setPreviewOfferId: editor.setPreviewOfferId,
		runPreview,
		save,
		requestDelete: setPendingDeleteId,
		confirmDelete,
		cancelDelete: () => setPendingDeleteId(null),
	}
}
